import { db } from '@/db';
import { runDayRollover } from '@/domain/day-rollover';
import { toLocalDateKey } from '@/domain/dates';
import { createId } from '@/domain/ids';
import { buildInitialSeed } from '@/domain/seeds';
import type { OnboardingData } from '@/features/onboarding';
import type { AppArea, TaskCategory, TimeOfDay, UserSettings } from '@/types';

let initialization: Promise<void> | null = null;

async function seedEmptyDatabase(): Promise<void> {
  if (await db.userSettings.count()) return;
  const today = toLocalDateKey(new Date());
  const now = new Date().toISOString();
  const seed = buildInitialSeed(today, now);
  await db.transaction(
    'rw',
    [
      db.userSettings,
      db.appPreferences,
      db.projects,
      db.taskTemplates,
      db.habits,
      db.schoolSubjects,
      db.studyTopics,
      db.novelProjects
    ],
    async () => {
      if (await db.userSettings.count()) return;
      await db.userSettings.add(seed.userSettings);
      await db.appPreferences.add(seed.appPreferences);
      await db.projects.bulkAdd(seed.projects);
      await db.taskTemplates.bulkAdd(seed.taskTemplates);
      await db.habits.bulkAdd(seed.habits);
      await db.schoolSubjects.bulkAdd(seed.schoolSubjects);
      await db.studyTopics.bulkAdd(seed.studyTopics);
      await db.novelProjects.add({
        id: createId(),
        projectId: seed.projectIds.novel,
        title: '200-Chapter Novel',
        targetChapters: 200,
        dailyChapterGoal: 8,
        currentArc: 'Erster Arc',
        continuityRules: [],
        notes: '',
        createdAt: now,
        updatedAt: now
      });
    }
  );
}

async function rolloverIfReady(): Promise<void> {
  const settings = await db.userSettings.toCollection().first();
  if (!settings?.onboardingCompleted) return;
  const preferences = await db.appPreferences.toCollection().first();
  const today = toLocalDateKey(new Date());
  await runDayRollover(db, { today, lastOpenedDate: preferences?.lastOpenedDate });
  if (preferences)
    await db.appPreferences.update(preferences.id, {
      lastOpenedDate: today,
      updatedAt: new Date().toISOString()
    });
}

export function initializeApp(): Promise<void> {
  initialization ??= (async () => {
    await db.open();
    await seedEmptyDatabase();
    await rolloverIfReady();
  })();
  return initialization;
}

const categoryArea: Partial<Record<TaskCategory, AppArea>> = {
  school: 'school',
  french: 'french',
  sport: 'sport',
  youtube: 'youtube',
  novel: 'novel',
  sleep: 'sleep',
  personal: 'personal'
};

export async function completeOnboarding(data: OnboardingData): Promise<void> {
  const settings = await db.userSettings.toCollection().first();
  if (!settings) throw new Error('Die lokalen Einstellungen konnten nicht geladen werden.');
  const now = new Date().toISOString();
  const today = toLocalDateKey(new Date());
  await db.transaction('rw', db.userSettings, db.taskTemplates, db.novelProjects, async () => {
    const updated: Partial<UserSettings> = {
      name: data.name.trim(),
      primaryGoal: data.primaryGoal.trim(),
      wakeTime: data.wakeTime as TimeOfDay,
      bedTime: data.sleepTime as TimeOfDay,
      activeAreas: [...data.areas] as AppArea[],
      dailyGoals: {
        schoolMinutes: data.dailyGoals.schoolMinutes,
        frenchMinutes: data.dailyGoals.frenchMinutes,
        youtubeMinutes: data.dailyGoals.youtubeMinutes,
        novelChapters: data.dailyGoals.novelChapters,
        pushUps: data.dailyGoals.pushUpCurrentMax,
        squats: data.dailyGoals.squatsDaily
      },
      currentPushUpMaximum: data.dailyGoals.pushUpCurrentMax,
      longTermPushUpGoal: data.dailyGoals.pushUpLongTermGoal,
      onboardingCompleted: true,
      onboardingCompletedAt: now,
      updatedAt: now
    };
    await db.userSettings.update(settings.id, updated);
    const templates = await db.taskTemplates.toArray();
    for (const template of templates) {
      const area = categoryArea[template.category];
      let target = template.defaultTargetValue;
      if (template.category === 'school') target = data.dailyGoals.schoolMinutes;
      if (template.category === 'french') target = data.dailyGoals.frenchMinutes;
      if (template.category === 'youtube') target = data.dailyGoals.youtubeMinutes;
      if (template.category === 'novel' && template.taskType === 'chapter')
        target = data.dailyGoals.novelChapters;
      let startTime = template.defaultStartTime;
      if (template.category === 'sleep' && template.taskType === 'habit') {
        startTime = data.wakeTime as TimeOfDay;
      }
      if (template.taskType === 'sleep') startTime = data.sleepTime as TimeOfDay;
      await db.taskTemplates.update(template.id, {
        active: area ? data.areas.includes(area) : template.active,
        defaultStartTime: startTime,
        defaultTargetValue: target,
        updatedAt: now
      });
    }
    const novel = await db.novelProjects.toCollection().first();
    if (novel)
      await db.novelProjects.update(novel.id, {
        dailyChapterGoal: data.dailyGoals.novelChapters,
        updatedAt: now
      });
  });
  const preferences = await db.appPreferences.toCollection().first();
  await runDayRollover(db, { today, lastOpenedDate: preferences?.lastOpenedDate });
  if (preferences)
    await db.appPreferences.update(preferences.id, { lastOpenedDate: today, updatedAt: now });
}
